import { TTaskBase } from "@ogaf/scripts/Base/TaskBase";
import { WellKnownMessages, DxType, ContractType } from "@ogaf/scripts/Modules/Enums";
import { TUiMessage } from "@ogaf/scripts/Modules/DataStructures";
import { TPpdApp } from "./PpdApp";
import renderReactComponent from "../React/ReactRenderer";
export class TPpdTaskBase extends TTaskBase {

    /**
    * Vlastnost, která v sobě drží hodnotu upload wave - kolikátý to tah přidání soubrů před uploadem
    */
    public uploadWave: number = 1;

    /**
     *  Vlastnost, která v sobě drží informace o souborech, které byly nahrány s chybou
     */
    public failedUploads: string[] = [];

    /**
     * Vytvoří instanci tasku a provede základní inicializaci přes base třídu.
     */
    constructor() {
        super();
    }

    /**
     * Vykreslí React komponentu pro výběr jazyka do elementu s ID `languageReactContainer`. 
     * Inicializační data obsahují seznam jazyků a callback pro změnu jazyka.
     */
    public renderLanguageSelector(): void {
        renderReactComponent('LanguageSelector', 'languageReactContainer',
            {
                initialData: {
                    LanguageEntries: [
                        {
                            Caption: 'Čeština',
                            Link: 'cs-CZ',
                            Icon: 'cs-CZ',
                            Order: 1,
                            Selected: true
                        }
                    ]
                },

                onLanguageSelect: function (link: string) {
                    window.location.reload();
                }
            }
        );
    }

    /**
    * Udalost vyvolana pri zmene role v Title panelu
    *
    * @param e Událost změny hodnoty SelectBoxu s rolí.
    */
    public override async cmbTitleRoles_valueChange(e: DevExpress.ui.dxSelectBox.ValueChangedEvent): Promise<void> {
        this.App.showLoadingPanel(false);

        try {
            const msg = new TUiMessage();
            msg.MessageType = WellKnownMessages.msg_Custom;
            msg.CustomMessageType = "msg_UserRoleChange";
            
            // Base třída očekává Dictionary<string, string>
            msg.MessageData = JSON.stringify({
                role: e.value,
                previousRole: e.previousValue
            });

            const response = await this.sendMessage(msg);
            const result = await response.text();

            if (result === "Role changed") {
                window.location.reload();
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.App.hideLoadingPanel();
        }
    }

    /**
     * Obecna metoda, ktera posila pozadavek na controller
     *
     * @param endpoint Relativní endpoint vůči aktuálnímu taskUrl.
     * @param options Konfigurace fetch requestu; pokud není zadáno, použije se POST s JSON hlavičkou.
     * @returns Promise, který se vyřeší po úspěšném dokončení requestu; při chybě vyhazuje výjimku.
     */
    public async apiFetch(endpoint: string, options: RequestInit = null): Promise<void> {
        try {
            if (options === null || options === undefined) {
                options = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: null
                };
            }
            let taskName = $('#hdnTaskName').val();
            let taskUrl = this.App.TaskMap.find(x => x.key == taskName);
            let url = taskUrl.uri + endpoint;
            const response = await fetch(url, options);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            throw error;
        }
    }

    /**
     * Vytvoří a odešle FE zprávu na server pro danou akci (CustomMessageType).
     * `payload` může být string nebo objekt; pokud není zadáno `url`, použije se endpoint z `TaskMap`.
     *
     * @param eventName Název akce (CustomMessageType) odesílaný na server.
     * @param payload Payload zprávy (string nebo objekt).
     * @param url Volitelná cílová URL; pokud není zadána, použije se `${taskUrl.uri}DigestMessage`.
     * @returns Response z fetch requestu.
     */
    protected dispatchToServer(eventName: string, payload?: any, url?: string): Promise<Response> {
        const msg = new TUiMessage();
        const type = (eventName ?? "").trim();

        msg.MessageType = WellKnownMessages.msg_ChangeDataView;
        msg.CustomMessageType = type;
        msg.ResponseContractType = ContractType.CompressedBo;
        msg.TaskName = String($("#hdnTaskName").val() ?? "");

        let payloadStr: string;

        if (payload == null)
            payloadStr = "";
        else if (typeof payload === "string")
            payloadStr = payload;
        else
            payloadStr = JSON.stringify(payload);

        msg.MessageData = payloadStr;

        return this.sendMessage(msg, url);
    }

    /**
     * Zajistí, že TaskMap je načtený před použitím.
     * Při přímém odkazu na task (article, ap.) se nenačítal ribbon
     */
    protected async getTaskMapAsync(): Promise<{ key: string, uri: string }[]> {
        const maxAttempts = 50; // 5 sekund max (50 * 100ms)
        let attempt = 0;

        while ((!this.App?.TaskMap || this.App.TaskMap.length === 0) && attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempt++;
        }

        if (!this.App?.TaskMap || this.App.TaskMap.length === 0) {
            throw new Error("TaskMap se nepodařilo načíst do 5 sekund");
        }

        return this.App.TaskMap;
    }

    /**
     * Odešle asynchronně zprávu `msg` na server (POST) na zadané `url`, případně na `${taskUrl.uri}DigestMessage` podle aktuálního `taskName` z `#hdnTaskName`.
     *
     * @param msg Zpráva k odeslání na server.
     * @param url Volitelná cílová URL.
     * @returns Response z fetch requestu; v případě výjimky vrátí Response se statusem 500.
     */
    protected async sendMessage(msg: TUiMessage, url?: string): Promise<Response> {
        try {
            const trimmedUrl = url?.trim();
            let targetUrl: string;

            if (trimmedUrl) {
                targetUrl = trimmedUrl;
            } else {
                const taskMap = await this.getTaskMapAsync();
                const taskName = String($("#hdnTaskName").val() ?? "").trim();
                const taskUrl = taskMap?.find(x => x.key === taskName);

                if (!taskUrl?.uri) {
                    throw new Error(`TaskMap URI not found for taskName='${taskName}'`);
                }

                targetUrl = `${taskUrl.uri}DigestMessage`;
            }

            return fetch(targetUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: msg.Json,
            });
        } catch (ex: unknown) {
            const message = ex instanceof Error ? ex.message : typeof ex === "string" ? ex : "Unknown error";

            return new Response(JSON.stringify({ error: true, message }), {
                status: 500,
                statusText: "Internal Server Error",
                headers: { "Content-Type": "application/json" },
            });
        }
    }

    /**
     * Načte a vykreslí aktuální podobu ribbonu (server-side render) do elementu `#ribbonComponentPanel`.
     *
     * @param init Určuje, zda jde o inicializaci (Index) nebo detail.
     * @param data Doplňující parametr (např. režim "create"/"edit").
     * @returns Promise, který se vyřeší po dopsání HTML do stránky.
     */
    public async CorrectRibbon(init: boolean, data: string = ""): Promise<void> {
        let taskName = $('#hdnTaskName').val();
        let taskUrl = this.App.TaskMap.find(x => x.key == taskName);
        let fetched = fetch(`${taskUrl.uri}CorrectRibbon?init=${init}&data=${data}`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
            body: null
        });
        let res = await fetched;
        let resText = res.text();
        resText.then((result) => {
            $("#ribbonComponentPanel").html(result);
        }).catch((result) => {
            console.error(result);
        });
    }

    /**
     * Metoda ovladající ribbon pro nastaveni nového / editace záznamu.
     * @param init Zobrazit ribbon pro index (true) nebo detail (false).
     * @param create Zobrazit ribbon pro nový záznam (true) nebo editace (false) záznamu.
     */
    public async CorrectRibbonChangeCancel(init: boolean, create: boolean = false): Promise < void> {
        if (init) {
            $(".rh-function").removeClass('d-none');
            var customBtn = $(".rh-function-legacy .mainRibbonGroup");
            customBtn.addClass('d-none');
            customBtn.next("li").addClass('d-none');
        } else {
            $(".rh-function").addClass('d-none');
            if (create) {
                $(".mainRibbonBtn[data-function-key='fnChange-create']").removeClass('d-none');
                $(".mainRibbonBtn[data-function-key='fnChange-change']").addClass('d-none');
            } else {
                $(".mainRibbonBtn[data-function-key='fnChange-create']").addClass('d-none');
                $(".mainRibbonBtn[data-function-key='fnChange-change']").removeClass('d-none');
            }
            var customBtn = $(".rh-function-legacy .mainRibbonGroup");
            customBtn.removeClass('d-none');
            customBtn.next("li").removeClass('d-none');
        }
    }

    /**
     * Vrátí hodnotu z DevExtreme instance komponenty dle `id` a `type`.
     *
     * @param id ID HTML elementu (bez #).
     * @param type Typ DevExtreme komponenty.
     * @returns Hodnota komponenty; pokud prvek neexistuje, vrací chybovou hlášku jako string.
     */
    public GetDxInstanceValue(id: string, type: DxType): (string | number | boolean | Date) {
        let value: string | number | boolean | Date;
        let instance = this.GetDxInstance(id, type);
        if (instance === null || instance === undefined)
            return `ERROR prvek typu ${type.toString()} s id: ${id} neexistuje!`;

        value = instance.option("value");
        if (value === null)
            return ""
        else
            return value;
    }

    /**
     * Nastaví hodnotu DevExtreme instance komponenty dle `id` a `type`.
     *
     * @param id ID HTML elementu (bez #).
     * @param type Typ DevExtreme komponenty.
     * @param value Hodnota, která se má nastavit.
     */
    public SetDxInstanceValue(id: string, type: DxType, value: (string | number | boolean | Date)): void {
        let instance = this.GetDxInstance(id, type);
        if (instance !== null && instance !== undefined) {
            instance.option("value", value);
        }
    }

    /**
     * Nastaví libovolnou option DevExtreme instance komponenty dle `id` a `type`.
     *
     * @param id ID HTML elementu (bez #).
     * @param type Typ DevExtreme komponenty.
     * @param option Název option.
     * @param value Hodnota option.
     */
    public SetDxInstanceOption(id: string, type: DxType, option: string, value: (string | number | boolean | Date)): void {
        let instance = this.GetDxInstance(id, type);
        if (instance !== null && instance !== undefined) {
            instance.option(option, value);
        }
    }

    /**
     * Vrátí DevExtreme instanci komponenty podle `id` a `type`.
     *
     * @param id ID HTML elementu (bez #).
     * @param type Typ DevExtreme komponenty.
     * @returns Instance komponenty nebo undefined, pokud se nepodaří získat.
     */
    public GetDxInstance(id: string, type: DxType): any {
        let instance: DevExpress.ui.Editor | DevExpress.ui.dxSelectBox | DevExpress.ui.dxDateBox | DevExpress.ui.dxTagBox | DevExpress.ui.dxDataGrid;
        switch (type) {
            case DxType.TextBox:
                instance = $(`#${id}`).dxTextBox("instance");
                break;
            case DxType.SelectBox:
                instance = $(`#${id}`).dxSelectBox("instance");
                break;
            case DxType.CheckBox:
                instance = $(`#${id}`).dxCheckBox("instance");
                break;
            case DxType.DateBox:
                instance = $(`#${id}`).dxDateBox("instance");
                break;
            case DxType.TextArea:
                instance = $(`#${id}`).dxTextArea("instance");
                break;
            case DxType.RadioGroup:
                instance = $(`#${id}`).dxRadioGroup("instance");
                break;
            case DxType.TagBox:
                instance = $(`#${id}`).dxTagBox("instance");
                break;
            case DxType.NumberBox:
                instance = $(`#${id}`).dxNumberBox("instance");
                break;
            case DxType.DropDownBox:
                instance = $(`#${id}`).dxDropDownBox("instance");
                break;
            case DxType.DataGrid:
                instance = $(`#${id}`).dxDataGrid("instance");
                break;
            default:
                break;
        }
        return instance;
    }

    /**
    * Funkce, která reaguje na klik tlačítka Přidat přílohu, vytvoří slovník s hodnotami pro přílohu
    * 
    * @param e Událost reagující na stisk tlačítka
    */
    public FileUploader_valueChanged(e: DevExpress.ui.dxFileUploader.ValueChangedEvent): void {
        const BYTES_IN_MB = 1_000_000;

        // Sečtu velikost všech vložených souborů v MB
        const filesMB = e.value.map(f => (f.size ?? 0) / BYTES_IN_MB);
        const totalMB = Math.round(filesMB.reduce((s, m) => s + m, 0));

        // Z hidden inputu vytáhnu, kolik MB je povoleno celkem
        const raw = String($("#hiddenMaxAllowedContentLength").val() ?? "");
        const allowedTotalMB = parseInt(raw, 10);

        if (e.value.length > 0 && e.value.length > e.previousValue.length) {
            const items = e.value;

            // Projdu pouze nově přidané soubory, abych je zkontroloval.
            for (let i = e.previousValue.length; i < e.value.length; i++) {
                const fileElement = $(".dx-fileuploader-file-container").eq(i);
                const name = items[i].name;
                const size = items[i].size;
                let errMsg = "";

                // Validace přípony
                if (e.element[0].innerText.includes(e.component.option("invalidFileExtensionMessage"))) {
                    errMsg = "není podporován";
                } // Validace velikosti daného souboru
                else if (e.element[0].innerText.includes(e.component.option("invalidMaxFileSizeMessage"))) {
                    errMsg = "přesáhl maximální povolenou velikost";
                } // Validace celkové velikosti upload batche
                else if (totalMB > allowedTotalMB) {
                    this.App.showMessageBoxEx("Upozornění",
                        `Celková velikost (${totalMB}MB / ${allowedTotalMB}MB) překročila limit.`, "", "", "OK");
                    e.component.reset();

                    return;
                }

                if (errMsg !== "") {
                    this.App.showMessageBoxEx("Upozornění", `Zvolený soubor "${name}" ${errMsg} a byl odebrán.`, "", "", "OK");
                    e.component.reset();

                    return;
                }

                // Kontrola, zda element už nemá cancel button
                if (!fileElement.find('.appended').length) {
                    const safeName = name.replace(/[^a-zA-Z0-9]/g, '').replace(/^[0-9]/, 'file$&');
                    let cancelButton = `<div id="id-${safeName}-${size}-${this.uploadWave}" class='dx-fileuploader-button-container appended' onclick="CurrentTask.FileUploaderCancelButton('${name}', '${size}', '${this.uploadWave}')"><div class='dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-icon dx-fileuploader-button dx-fileuploader-cancel-button'><div class='dx-button-content'><i class='dx-icon dx-icon-close'></i><div class='dx-inkripple'></div></div></div></div>`;

                    fileElement.append(cancelButton);
                }
            };

            this.uploadWave++;
        }
    }


    /**
 * Provede refresh AllUserRoles view po updatu
 */
    public async refreshAllUserRolesView(): Promise<void> {
        const taskName = "taskMetadataRefresh";
        const task = this.App.TaskMap.find(x => x.key === taskName);
        const url = `${task.uri}DoRefresh`;

        const payload = {
            type: "metadata",
            selectedView: "AllUserRoles"
        };

        const res = await fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data: JSON.stringify(payload) })
        });

        if (!res.ok)
            console.error(`HTTP ${res.status}`);
    }

    /**
     * Presmerovani na home stranku.
     */
    public HomeRedirect(): void {
        window.location.href = "/Home/Index";
    }

    /**
     * Obslužná metoda při kluknutí na horizontální menu. Při kliknutí na modul se zobrazí nabídka tasku.
     * Při kliknutí na task se provede redirect.
     * @param type Typ kliknuti - Module / Task
     * @param key Klíč položky, na kterou se kliklo.
     */
    public async RibbonHorizontalMenuClick(element: HTMLElement, type: string, key: string): Promise<void> {
        try {
            var typeLower = type.toLowerCase();
            if (typeLower.includes("link")) {
                var app = this.App as TPpdApp;
                app.openLinkInNewWindow(key);
            }
            else if (typeLower.includes("task")) {
                const url = this.App.TaskMap.find(x => x.key == key);
                window.location.href = url.uri+"Index";
            } else if (typeLower.includes("module")) {
                const menu = $(element).closest(".rh-menu");
                menu.find(".rh-menu-item.active").removeClass("active");
                $(element).addClass("active");

                const hideDiv = $(".rh-submenu:not(.d-none)");
                hideDiv.addClass("d-none");

                const showDiv = $(".rh-submenu[data-module-key='" + key + "']");
                showDiv.removeClass("d-none");
            } else {
                console.log("Neznámá chyba. V obsluze ribbon horizontal menu - neznámá udalost: " + type);
            }
        } catch (e) {
            console.log(e);
        }
    }

    /**
     * Obsluha tlacitka. Prepinani zobrazeni funkci.
     */
    public async RibbonHorizontalToggleClick() : Promise<void> {
        try {
            const tglButton = $(".rh-toggle-button");
            const functions = $(".main-ribbon-content");            
            const showed = !functions.hasClass("d-none");
            console.log(showed);
            if (showed) {
                tglButton.removeClass("bi-caret-up-fill");
                functions.addClass("d-none");
                tglButton.addClass("bi-caret-down-fill");
            } else {
                tglButton.removeClass("bi-caret-down-fill");
                functions.removeClass("d-none");
                tglButton.addClass("bi-caret-up-fill");
            }

        } catch (e) {
            console.log(e);
        }
    }
}

/**
 * Inicialize funkce. Po nacteni stranky je potreba custom pridat funcknost pri kliknuti na logo.
 */
document.addEventListener("DOMContentLoaded", () => {
    let logoImg = $("div.titleBar-logo-app img");
    if (logoImg != null && logoImg != undefined) {
        logoImg.attr("onclick", "CurrentTask.HomeRedirect()");
        logoImg.attr("style", "cursor:pointer;")
    }
});